package com.schoolconnect.app.feature.schools

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.schoolconnect.app.designsystem.AppBackground
import com.schoolconnect.app.designsystem.AppButton
import com.schoolconnect.app.designsystem.AppBorder
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppDangerSoft
import com.schoolconnect.app.designsystem.AppInfoBg
import com.schoolconnect.app.designsystem.AppLine
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppOutlinedButton
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppPrimarySoft
import com.schoolconnect.app.designsystem.AppSecondaryBorder
import com.schoolconnect.app.designsystem.AppSurface
import com.schoolconnect.app.designsystem.AppSuccess
import com.schoolconnect.app.designsystem.AppSuccessSoft
import com.schoolconnect.app.designsystem.AppDialogStandardWidth
import com.schoolconnect.app.designsystem.AppDialogBody
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogDefaultColors
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.designsystem.list.AdminInfoBanner
import com.schoolconnect.app.designsystem.list.AdminStandardRowActionMenu
import com.schoolconnect.app.designsystem.list.AdminStatusChip
import com.schoolconnect.app.designsystem.list.onViewDoubleClick
import ischoolconnect.sharedui.generated.resources.arrow_up
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.building_school
import ischoolconnect.sharedui.generated.resources.chevron_right
import ischoolconnect.sharedui.generated.resources.info_circle
import ischoolconnect.sharedui.generated.resources.more_vertical
import ischoolconnect.sharedui.generated.resources.search
import kotlin.math.abs
import kotlin.math.roundToInt
import org.jetbrains.compose.resources.painterResource
import com.schoolconnect.app.designsystem.list.AdminListPagination
import com.schoolconnect.app.designsystem.list.AdminListSearchField
import com.schoolconnect.app.designsystem.list.AdminListSortColumn
import com.schoolconnect.app.repository.systemlookup.SystemLookupOptions
import com.schoolconnect.app.designsystem.list.AdminListSortHeaderRow
import com.schoolconnect.app.designsystem.list.AdminListRowActionBar
import com.schoolconnect.app.designsystem.list.AdminListTableBodyText
import com.schoolconnect.app.designsystem.list.AdminListToolbarButton
import com.schoolconnect.app.feature.common.compareBySmartText
import com.schoolconnect.app.feature.common.toWindow

internal val SchoolsBackground = AppBackground
internal val SchoolsSurface = AppSurface
internal val SchoolsBorder = AppSecondaryBorder
internal val SchoolsTextPrimary = AppNavy
internal val SchoolsTextSecondary = AppMuted
internal val SchoolsTextMuted = Color(0xFF7D86A9)
internal val SchoolsBlue = AppPrimary
internal val SchoolsBlueSoft = AppPrimarySoft
internal val SchoolsGreen = AppSuccess
internal val SchoolsGreenSoft = AppSuccessSoft
internal val SchoolsOrange = Color(0xFFF59E0B)
internal val SchoolsOrangeSoft = Color(0xFFFFF1E0)
internal val SchoolsRed = AppDanger
internal val SchoolsRedSoft = AppDangerSoft

internal data class SchoolStats(
    val total: Int,
    val active: Int,
    val inactive: Int
)

internal data class SchoolViewModelPage(
    val items: List<SchoolRowUi>,
    val totalCount: Int,
    val startIndex: Int,
    val endIndex: Int,
    val pageCount: Int
)

internal enum class SchoolAction {
    VIEW_DETAILS,
    EDIT,
    MANAGE_USERS,
    TOGGLE_STATUS,
    DELETE
}

internal fun schoolStats(items: List<SchoolRowUi>): SchoolStats {
    val active = items.count { it.isActive }
    return SchoolStats(
        total = items.size,
        active = active,
        inactive = items.size - active
    )
}

internal fun schoolFilterLabels(searchQuery: String, filters: SchoolFilterCriteria): List<String> = buildList {
    if (searchQuery.isNotBlank()) add("Search: ${searchQuery.trim()}")
    if (filters.board.isNotBlank()) add("Board: ${filters.board.trim()}")
    if (filters.schoolType.isNotBlank()) add("Type: ${filters.schoolType.trim()}")
    if (filters.medium.isNotBlank()) add("Medium: ${filters.medium.trim()}")
    if (filters.minStudents.isNotBlank()) add("Min students: ${filters.minStudents.trim()}")
    if (filters.maxStudents.isNotBlank()) add("Max students: ${filters.maxStudents.trim()}")
    if (filters.createdFrom.isNotBlank()) add("Created from: ${filters.createdFrom.trim()}")
    if (filters.createdTo.isNotBlank()) add("Created to: ${filters.createdTo.trim()}")
    if (filters.updatedFrom.isNotBlank()) add("Updated from: ${filters.updatedFrom.trim()}")
    if (filters.updatedTo.isNotBlank()) add("Updated to: ${filters.updatedTo.trim()}")
}

/** Match a stored lookup code against a filter that may be either a code or display label. */
private fun matchesLookupFilter(storedCode: String, filterLabelOrCode: String, category: String): Boolean {
    if (filterLabelOrCode.isBlank()) return true
    val filter = filterLabelOrCode.trim()
    if (storedCode.equals(filter, ignoreCase = true)) return true
    val resolvedCode = SystemLookupOptions.code(category, filter)
    return resolvedCode.isNotBlank() && storedCode.equals(resolvedCode, ignoreCase = true)
}

internal fun filteredAndPagedSchools(
    state: SchoolsUiState
): SchoolViewModelPage {
    val page = state.schools
        .asSequence()
        .filter { school ->
            val filters = state.filters
            val matchesSearch = state.searchQuery.isBlank() || buildList {
                add(school.name)
                add(school.code)
                add(school.city)
                add(school.contactName)
                add(school.board)
                add(school.schoolType)
                add(school.medium)
                add(school.location)
            }.joinToString(" ").contains(state.searchQuery.trim(), ignoreCase = true)

            val matchesStatus = when (filters.status) {
                SchoolStatusFilter.ALL -> true
                SchoolStatusFilter.ACTIVE -> school.isActive
                SchoolStatusFilter.INACTIVE -> !school.isActive
            }

            val matchesBoard = matchesLookupFilter(school.board, filters.board, "SCHOOL_BOARD")
            val matchesSchoolType = matchesLookupFilter(school.schoolType, filters.schoolType, "SCHOOL_TYPE")
            val matchesMedium = matchesLookupFilter(school.medium, filters.medium, "SCHOOL_MEDIUM")

            val minStudents = filters.minStudents.trim().toIntOrNull()
            val maxStudents = filters.maxStudents.trim().toIntOrNull()
            val matchesStudentRange = (minStudents == null || (school.students ?: 0) >= minStudents) &&
                (maxStudents == null || (school.students ?: 0) <= maxStudents)

            val createdDate = school.createdOn.toFilterDateNumber()
            val updatedDate = school.updatedOn.toFilterDateNumber()
            val createdFrom = filters.createdFrom.toFilterDateNumber()
            val createdTo = filters.createdTo.toFilterDateNumber()
            val updatedFrom = filters.updatedFrom.toFilterDateNumber()
            val updatedTo = filters.updatedTo.toFilterDateNumber()
            val matchesCreatedRange = (createdFrom == null || (createdDate != null && createdDate >= createdFrom)) &&
                (createdTo == null || (createdDate != null && createdDate <= createdTo))
            val matchesUpdatedRange = (updatedFrom == null || (updatedDate != null && updatedDate >= updatedFrom)) &&
                (updatedTo == null || (updatedDate != null && updatedDate <= updatedTo))

            matchesSearch &&
                matchesStatus &&
                matchesBoard &&
                matchesSchoolType &&
                matchesMedium &&
                matchesStudentRange &&
                matchesCreatedRange &&
                matchesUpdatedRange
        }
        .toList()

    val window = state.paging.toWindow(page.size)

    return SchoolViewModelPage(
        items = page,
        totalCount = window.totalCount,
        startIndex = window.startIndex,
        endIndex = window.endIndex,
        pageCount = window.pageCount
    )
}

private fun String.toFilterDateNumber(): Int? {
    val value = trim()
    if (value.isBlank()) return null
    val numeric = value.take(10).split("-")
    if (numeric.size == 3 && numeric[0].length == 2 && numeric[2].length == 4) {
        return "${numeric[2]}${numeric[1]}${numeric[0]}".toIntOrNull()
    }
    val readable = value.split(" ")
    if (readable.size == 3) {
        val month = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
            .indexOfFirst { it.equals(readable[1], ignoreCase = true) } + 1
        if (month > 0) return "${readable[2]}${month.toString().padStart(2, '0')}${readable[0].padStart(2, '0')}".toIntOrNull()
    }
    return null
}

private fun schoolsComparator(
    field: SchoolTableSortField,
    ascending: Boolean
): Comparator<SchoolRowUi> {
    val comparator = when (field) {
        SchoolTableSortField.NAME -> compareBySmartText<SchoolRowUi> { it.name }
        SchoolTableSortField.CODE -> compareBySmartText<SchoolRowUi> { it.code }
        SchoolTableSortField.CITY -> compareBySmartText<SchoolRowUi> { it.city }
        SchoolTableSortField.CONTACT -> compareBySmartText<SchoolRowUi> { it.contactName }
        SchoolTableSortField.STUDENTS -> compareBy<SchoolRowUi> { it.students ?: -1 }
        SchoolTableSortField.STATUS -> compareBy<SchoolRowUi> { if (it.isActive) 0 else 1 }
    }
    return if (ascending) comparator else comparator.reversed()
}

internal fun schoolEditorDraft(item: SchoolRowUi?): SchoolEditorDraft = if (item == null) {
    SchoolEditorDraft()
} else {
    SchoolEditorDraft(
        id = item.id,
        name = item.name,
        code = item.code,
        city = item.city,
        contactName = item.contactName,
        students = item.students?.toString().orEmpty(),
        isActive = item.isActive
    )
}

internal fun schoolStatusLabel(isActive: Boolean): String = if (isActive) "Active" else "Inactive"

internal fun schoolStatusColors(isActive: Boolean): Pair<Color, Color> =
    if (isActive) SchoolsGreen to SchoolsGreenSoft else SchoolsRed to SchoolsRedSoft

internal fun schoolDetailValue(value: String?): String = value?.takeIf { it.isNotBlank() } ?: "-"

internal fun schoolStudentCountText(value: Int?): String = value?.toString() ?: "-"

@Composable
internal fun SchoolStatCard(
    title: String,
    value: String,
    subtitle: String,
    accent: Color,
    accentSoft: Color,
    icon: androidx.compose.ui.graphics.painter.Painter,
    modifier: Modifier = Modifier
) {
    Card(
        modifier = modifier,
        shape = RoundedCornerShape(18.dp),
        colors = CardDefaults.cardColors(containerColor = SchoolsSurface),
        border = androidx.compose.foundation.BorderStroke(1.dp, SchoolsBorder)
    ) {
        Row(
            modifier = Modifier.padding(20.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(18.dp)
        ) {
            Box(
                modifier = Modifier
                    .size(60.dp)
                    .background(accentSoft, RoundedCornerShape(16.dp)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = icon,
                    contentDescription = title,
                    tint = accent
                )
            }
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.bodyMedium,
                    color = SchoolsTextPrimary,
                    fontWeight = FontWeight.Medium
                )
                Text(
                    text = value,
                    style = MaterialTheme.typography.headlineSmall,
                    color = SchoolsTextPrimary,
                    fontWeight = FontWeight.Bold
                )
                Text(
                    text = subtitle,
                    style = MaterialTheme.typography.bodyMedium,
                    color = accent
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun SchoolSearchField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    AdminListSearchField(
        value = value,
        onValueChange = onValueChange,
        placeholder = "Search by school name, code or city...",
        modifier = modifier,
        width = 420.dp,
        surfaceColor = SchoolsSurface,
        borderColor = SchoolsBorder,
        iconTint = SchoolsTextSecondary,
        textColor = SchoolsTextPrimary,
        cursorColor = SchoolsBlue
    )
}

@Composable
internal fun SchoolToolbarButton(
    label: String,
    icon: androidx.compose.ui.graphics.painter.Painter,
    onClick: () -> Unit,
    filled: Boolean,
    modifier: Modifier = Modifier
) {
    if (filled) {
        AppButton(
            onClick = onClick,
            modifier = modifier,
            shape = RoundedCornerShape(12.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = SchoolsBlue,
                contentColor = Color.White
            )
        ) {
            Icon(painter = icon, contentDescription = label, tint = Color.White)
            Spacer(modifier = Modifier.size(8.dp))
            Text(label)
        }
    } else {
        AdminListToolbarButton(
            label = label,
            icon = icon,
            onClick = onClick,
            modifier = modifier,
            width = 120.dp,
            borderColor = SchoolsBorder,
            contentColor = SchoolsBlue,
            iconSize = 18.dp
        )
    }
}

@Composable
internal fun SchoolStatusChip(
    active: Boolean,
    modifier: Modifier = Modifier
) {
    val (textColor, _) = schoolStatusColors(active)
    AdminStatusChip(
        label = schoolStatusLabel(active),
        contentColor = textColor,
        backgroundColor = Color.Transparent,
        modifier = modifier
    )
}

@Composable
internal fun SchoolHeaderRow(
    sortField: SchoolTableSortField,
    sortAscending: Boolean,
    onSortChanged: (SchoolTableSortField) -> Unit
) {
    AdminListSortHeaderRow(
        columns = listOf(
            AdminListSortColumn(
                label = "School Name",
                weight = 1.8f,
                selected = sortField == SchoolTableSortField.NAME,
                ascending = sortAscending,
                onClick = { onSortChanged(SchoolTableSortField.NAME) }
            ),
            AdminListSortColumn(
                label = "Code",
                weight = 1.0f,
                selected = sortField == SchoolTableSortField.CODE,
                ascending = sortAscending,
                onClick = { onSortChanged(SchoolTableSortField.CODE) }
            ),
            AdminListSortColumn(
                label = "City",
                weight = 1.0f,
                selected = sortField == SchoolTableSortField.CITY,
                ascending = sortAscending,
                onClick = { onSortChanged(SchoolTableSortField.CITY) }
            ),
            AdminListSortColumn(
                label = "Contact Name",
                weight = 1.2f,
                selected = sortField == SchoolTableSortField.CONTACT,
                ascending = sortAscending,
                onClick = { onSortChanged(SchoolTableSortField.CONTACT) }
            ),
            AdminListSortColumn(
                label = "Students",
                weight = 0.8f,
                selected = sortField == SchoolTableSortField.STUDENTS,
                ascending = sortAscending,
                onClick = { onSortChanged(SchoolTableSortField.STUDENTS) }
            ),
            AdminListSortColumn(
                label = "Status",
                weight = 0.9f,
                selected = sortField == SchoolTableSortField.STATUS,
                ascending = sortAscending,
                onClick = { onSortChanged(SchoolTableSortField.STATUS) }
            )
        ),
        actionsWeight = 0.95f,
        height = 44.dp,
        horizontalPadding = 24.dp,
        labelColor = SchoolsTextPrimary,
        selectedColor = SchoolsBlue,
        unselectedColor = SchoolsTextSecondary
    )
}

@Composable
internal fun SchoolRowCard(
    school: SchoolRowUi,
    onViewDetails: () -> Unit,
    onEdit: () -> Unit,
    onToggleStatus: () -> Unit,
    onDelete: () -> Unit,
    modifier: Modifier = Modifier
) {
    var menuExpanded by remember { mutableStateOf(false) }
    Column(modifier = modifier.fillMaxWidth().onViewDoubleClick(onViewDetails)) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(52.dp)
                .padding(horizontal = 24.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            AdminListTableBodyText(
                text = school.name,
                modifier = Modifier.weight(1.8f),
                color = SchoolsTextPrimary
            )
            AdminListTableBodyText(
                text = school.code,
                modifier = Modifier.weight(1.0f),
                color = SchoolsTextPrimary
            )
            AdminListTableBodyText(
                text = school.city,
                modifier = Modifier.weight(1.0f),
                color = SchoolsTextPrimary
            )
            AdminListTableBodyText(
                text = school.contactName,
                modifier = Modifier.weight(1.2f),
                color = SchoolsTextPrimary
            )
            AdminListTableBodyText(
                text = schoolStudentCountText(school.students),
                modifier = Modifier.weight(0.8f),
                color = SchoolsTextPrimary
            )
            Box(modifier = Modifier.weight(0.9f)) {
                SchoolStatusChip(active = school.isActive)
            }
            Box(modifier = Modifier.weight(0.95f), contentAlignment = Alignment.CenterStart) {
                AdminListRowActionBar(
                    isActive = school.isActive,
                    expanded = menuExpanded,
                    onExpandedChange = { menuExpanded = it },
                    onDismissRequest = { menuExpanded = false },
                    onView = {
                        menuExpanded = false
                        onViewDetails()
                    },
                    onEdit = {
                        menuExpanded = false
                        onEdit()
                    },
                    onToggleStatus = {
                        menuExpanded = false
                        onToggleStatus()
                    },
                    onDelete = {
                        menuExpanded = false
                        onDelete()
                    },
                    width = 160.dp,
                    surfaceColor = SchoolsSurface,
                    borderColor = SchoolsBorder
                )
            }
        }
        HorizontalDivider(color = SchoolsBorder, thickness = 1.dp)
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun SchoolPagination(
    pageIndex: Int,
    pageCount: Int,
    onPageChanged: (Int) -> Unit,
    modifier: Modifier = Modifier
) {
    AdminListPagination(
        pageIndex = pageIndex,
        pageCount = pageCount,
        onPageChanged = onPageChanged,
        modifier = modifier,
        borderColor = SchoolsBorder,
        surfaceColor = SchoolsSurface,
        primaryColor = SchoolsBlue,
        navyColor = SchoolsTextPrimary,
        mutedColor = SchoolsTextSecondary
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun SchoolInfoBanner(
    message: String,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier
) {
    AdminInfoBanner(
        message = message,
        onDismiss = onDismiss,
        modifier = modifier,
        leadingIcon = painterResource(Res.drawable.info_circle),
        backgroundColor = SchoolsBlueSoft,
        borderColor = SchoolsBorder,
        contentColor = SchoolsTextPrimary,
        accentColor = SchoolsBlue
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun SchoolDetailDialog(
    school: SchoolRowUi,
    onEdit: () -> Unit,
    onToggleStatus: () -> Unit,
    onDelete: () -> Unit,
    onDismiss: () -> Unit
) {
    AppDialogScaffold(onDismiss = onDismiss, size = AppDialogSize.Standard, maxDialogHeight = 520.dp) {
        AppDialogHeader(
            title = school.name,
            subtitle = "${school.code} • ${school.city}",
            onClose = onDismiss,
            colors = AppDialogDefaultColors,
            closeContentDescription = "Close school details"
        )
        AppDialogDivider()
        AppDialogBody(horizontalPadding = 24.dp, verticalPadding = 24.dp) {
            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                DetailLine("Contact", school.contactName)
                DetailLine("Students", schoolStudentCountText(school.students))
                DetailLine("Status", schoolStatusLabel(school.isActive))
            }
        }
        AppDialogDivider()
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction("Close", onDismiss),
                AppDialogFooterAction("Delete", onDelete, AppDialogActionStyle.Danger),
                AppDialogFooterAction(if (school.isActive) "Suspend" else "Activate", onToggleStatus),
                AppDialogFooterAction("Edit", onEdit, AppDialogActionStyle.Primary)
            )
        )
    }
}

@Composable
private fun DetailLine(
    label: String,
    value: String
) {
    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodySmall,
            color = SchoolsTextSecondary,
            fontWeight = FontWeight.Medium
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            color = SchoolsTextPrimary,
            fontWeight = FontWeight.SemiBold
        )
    }
}

internal fun sampleSchoolRows(): List<SchoolRowUi> = listOf(
    SchoolRowUi("1", "Aurora Public School", "AUR5001", "Delhi", "Meera Iyer", 0, true, "CBSE", "High School", "English", "Delhi", "10-06-2024", "08-06-2026"),
    SchoolRowUi("2", "Camara High School", "3456", "Mumbai", "Mohandas Pal", 2, true, "State Board", "High School", "Hindi", "Mumbai", "21-01-2024", "09-06-2026"),
    SchoolRowUi("3", "Demo School 607171", "DM607171", "Bangalore", "Principal Demo", 30, true, "ICSE", "Higher Secondary", "English", "Bangalore", "12-09-2023", "02-06-2026"),
    SchoolRowUi("4", "Greenfield International School", "GFS2028", "Hyderabad", "Dr. Meera Nair", 500, true, "IB", "Higher Secondary", "English", "Hyderabad", "08-04-2023", "10-06-2026"),
    SchoolRowUi("5", "NextGen Public School", "NXT9001", "Pune", "Anita Verma", 9, true, "CBSE", "Primary School", "Marathi", "Pune", "25-07-2024", "11-06-2026"),
    SchoolRowUi("6", "Orchid International School", "ORC1001", "Chennai", "Amulya Pal", 2, true, "IGCSE", "Pre-School", "Tamil", "Chennai", "18-08-2024", "05-06-2026"),
    SchoolRowUi("7", "Riverdale International School", "RIV4201", "Kolkata", "Nandita Rao", 6, true, "ICSE", "High School", "English", "Kolkata", "29-05-2024", "08-06-2026"),
    SchoolRowUi("8", "Vidyadrahinee School", "VID5750", "Jaipur", "Uma Bhat", 801, true, "State Board", "Higher Secondary", "Hindi", "Jaipur", "11-02-2024", "07-06-2026"),
    SchoolRowUi("9", "Sunrise Convent School", "SUN7802", "Ahmedabad", "Rajeev Mehta", 120, false, "CBSE", "High School", "English", "Ahmedabad", "03-03-2023", "04-06-2026"),
    SchoolRowUi("10", "Bright Future Academy", "BFA1203", "Lucknow", "Suresh Tiwari", 75, false, "State Board", "Primary School", "Hindi", "Lucknow", "13-01-2024", "01-06-2026"),
    SchoolRowUi("11", "Maple Leaf Academy", "MLA4401", "Coimbatore", "Asha Menon", 314, true, "IB", "High School", "English", "Coimbatore", "06-11-2023", "06-06-2026"),
    SchoolRowUi("12", "St. Xavier's Senior Secondary", "SXS2100", "Bhopal", "Rohit Dutta", 650, true, "ICSE", "Higher Secondary", "English", "Bhopal", "17-07-2023", "09-06-2026"),
    SchoolRowUi("13", "Lotus Valley Public", "LVP3007", "Noida", "Nitin Kapoor", 242, true, "CBSE", "High School", "English", "Noida", "04-10-2023", "12-06-2026"),
    SchoolRowUi("14", "Horizon International", "HZN8100", "Gurugram", "Priya Sharma", 194, true, "IGCSE", "High School", "English", "Gurugram", "14-08-2024", "08-06-2026"),
    SchoolRowUi("15", "Silver Oaks School", "SOV7702", "Mysuru", "Sanjay Rao", 88, true, "State Board", "Primary School", "Kannada", "Mysuru", "19-06-2024", "02-06-2026"),
    SchoolRowUi("16", "Oakridge World School", "OKR9050", "Visakhapatnam", "Lakshmi Devi", 412, true, "CBSE", "Higher Secondary", "English", "Visakhapatnam", "09-05-2023", "05-06-2026"),
    SchoolRowUi("17", "Little Angels Public", "LAP5502", "Thiruvananthapuram", "Dinesh Kumar", 154, true, "ICSE", "Primary School", "Malayalam", "Thiruvananthapuram", "23-03-2024", "07-06-2026"),
    SchoolRowUi("18", "Pinecrest School", "PCS2601", "Nagpur", "Nisha Deshmukh", 64, true, "State Board", "Pre-School", "Marathi", "Nagpur", "27-12-2023", "03-06-2026"),
    SchoolRowUi("19", "Delhi Public Academy", "DPA3402", "New Delhi", "Arvind Malhotra", 921, true, "CBSE", "Higher Secondary", "English", "New Delhi", "15-02-2023", "13-06-2026"),
    SchoolRowUi("20", "Harmony High", "HHY1004", "Surat", "Pooja Nair", 210, true, "ICSE", "High School", "English", "Surat", "30-09-2023", "06-06-2026"),
    SchoolRowUi("21", "New Era School", "NES4409", "Kochi", "Ramesh Babu", 133, true, "CBSE", "Primary School", "Malayalam", "Kochi", "05-04-2024", "08-06-2026"),
    SchoolRowUi("22", "Summit Public", "SPT9044", "Indore", "Kavita Singh", 287, true, "State Board", "High School", "Hindi", "Indore", "22-10-2023", "04-06-2026"),
    SchoolRowUi("23", "Lakeview School", "LKS3003", "Bhubaneswar", "Aditya Sen", 418, true, "CBSE", "Higher Secondary", "English", "Bhubaneswar", "08-11-2023", "11-06-2026"),
    SchoolRowUi("24", "Crescent Academy", "CRA8011", "Patna", "Farah Khan", 97, true, "ICSE", "Primary School", "Hindi", "Patna", "18-01-2024", "10-06-2026")
)

internal fun schoolCountSummary(items: List<SchoolRowUi>): String {
    val stats = schoolStats(items)
    val activeRatio = if (stats.total == 0) 0.0 else (stats.active.toDouble() / stats.total.toDouble()) * 100.0
    return "${activeRatio.formatTwoDecimals()}% of total"
}

internal fun percentageText(part: Int, total: Int): String {
    if (total <= 0) return "0.00"
    val value = (part.toDouble() / total.toDouble()) * 100.0
    return value.formatTwoDecimals()
}

private fun Double.formatTwoDecimals(): String {
    val scaled = (this * 100).roundToInt()
    val whole = scaled / 100
    val fraction = abs(scaled % 100)
    return whole.toString() + "." + fraction.toString().padStart(2, '0')
}
